import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { getMyCommunities, shareToСommunity } from '../lib/communityService';

const APP_URL = window.location.origin;

// ── Create a public shareable URL for a text lesson ───────────────────────────
export async function createShareableLesson(lesson, subject, userId) {
  const { data, error } = await supabase
    .from('shared_lessons')
    .insert({ lesson_data: lesson, subject, shared_by: userId || null })
    .select('id')
    .single();
  if (error || !data) return null;
  return `${APP_URL}?lesson=${data.id}`;
}

// ── All 8 platform definitions ────────────────────────────────────────────────
function buildPlatforms(title, url, text) {
  const enc = {
    url:   encodeURIComponent(url),
    text:  encodeURIComponent(text),
    title: encodeURIComponent(title),
    full:  encodeURIComponent(`${text}\n\n${url}`),
  };
  return [
    {
      id:    'facebook',
      name:  'Facebook',
      color: '#1877F2',
      emoji: '📘',
      href:  `https://www.facebook.com/sharer/sharer.php?u=${enc.url}&quote=${enc.text}`,
    },
    {
      id:    'messenger',
      name:  'Messenger',
      color: '#0084FF',
      emoji: '💙',
      href:  `fb-messenger://share/?link=${enc.url}`,
    },
    {
      id:    'whatsapp',
      name:  'WhatsApp',
      color: '#25D366',
      emoji: '💬',
      href:  `https://wa.me/?text=${enc.full}`,
    },
    {
      id:    'sms',
      name:  'SMS',
      color: '#a855f7',
      emoji: '📱',
      href:  null, // handled via phone-number input
    },
    {
      id:    'email',
      name:  'Email',
      color: '#94a3b8',
      emoji: '✉️',
      href:  `mailto:?subject=${encodeURIComponent('Check this out: ' + title)}&body=${encodeURIComponent(text + '\n\n' + url)}`,
    },
    {
      id:    'linkedin',
      name:  'LinkedIn',
      color: '#0A66C2',
      emoji: '💼',
      href:  `https://www.linkedin.com/sharing/share-offsite/?url=${enc.url}&title=${enc.title}&summary=${enc.text}`,
    },
    {
      id:    'twitter',
      name:  'X / Twitter',
      color: '#e2e8f0',
      emoji: '🐦',
      href:  `https://twitter.com/intent/tweet?text=${enc.text}&url=${enc.url}`,
    },
    {
      id:    'instagram',
      name:  'Instagram',
      color: '#E1306C',
      emoji: '📸',
      href:  null, // copies caption to clipboard
    },
  ];
}

// ── Community picker (inline step) ────────────────────────────────────────────
function CommunityPickerView({ content, onBack, onClose }) {
  const { user } = useAuth();
  const [communities, setCommunities] = useState([]);
  const [selected,    setSelected]    = useState('');
  const [message,     setMessage]     = useState('');
  const [loading,     setLoading]     = useState(true);
  const [sharing,     setSharing]     = useState(false);
  const [shared,      setShared]      = useState(false);

  useEffect(() => {
    getMyCommunities(user.id).then(({ data }) => {
      setCommunities(data || []);
      if (data?.length === 1) setSelected(data[0].id);
      setLoading(false);
    });
  }, [user.id]);

  const handleShare = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSharing(true);
    await shareToСommunity({ communityId: selected, userId: user.id, content, message });
    setSharing(false);
    setShared(true);
    setTimeout(onClose, 1500);
  };

  return (
    <div className="share-community-view">
      <button className="share-community-back" onClick={onBack}>← Back</button>
      <h4 className="share-community-heading">🌐 Share with Community</h4>

      {loading && <div className="hp-empty"><div className="hp-spinner" /></div>}

      {!loading && communities.length === 0 && (
        <div className="hp-empty" style={{ padding: '1rem 0' }}>
          <span className="hp-empty-icon">🌐</span>
          <p>You're not in any communities yet.</p>
          <p className="hp-empty-sub">Start one from any search result!</p>
        </div>
      )}

      {!loading && communities.length > 0 && !shared && (
        <form onSubmit={handleShare} className="share-community-form">
          <div className="auth-field">
            <label>Choose a community</label>
            <select
              value={selected}
              onChange={e => setSelected(e.target.value)}
              className="model-select"
              required
            >
              <option value="">— select —</option>
              {communities.map(c => (
                <option key={c.id} value={c.id}>{c.name} · {c.subject}</option>
              ))}
            </select>
          </div>
          <div className="auth-field">
            <label>Message <span className="auth-optional">(optional)</span></label>
            <input
              type="text"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="What did you find interesting?"
              disabled={sharing}
              maxLength={280}
            />
          </div>
          <button
            type="submit"
            className="auth-btn auth-btn--primary"
            disabled={sharing || !selected}
          >
            {sharing ? 'Sharing…' : '🚀 Share with Community'}
          </button>
        </form>
      )}

      {shared && (
        <div className="comm-share-success">
          <span>✅</span>
          <p>Shared with your community!</p>
        </div>
      )}
    </div>
  );
}

// ── Main ShareModal ───────────────────────────────────────────────────────────
function ShareModal({ title, url, description, content, onClose }) {
  const [view,            setView]            = useState('main'); // 'main' | 'community'
  const [copied,          setCopied]          = useState(false);
  const [instagramCopied, setInstagramCopied] = useState(false);
  const [showSmsInput,    setShowSmsInput]    = useState(false);
  const [smsPhone,        setSmsPhone]        = useState('');

  const shareUrl  = url || APP_URL;
  const shareText = `📚 I just learned about "${title}" on BiteLearning!\n\n${description ? description + '\n\n' : ''}🎓 Learn anything in minutes →`;
  const platforms = buildPlatforms(title, shareUrl, shareText);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (view === 'community') setView('main');
        else onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, view]);

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(shareUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleInstagram = async () => {
    const caption = `${shareText}\n\n${shareUrl}`;
    await navigator.clipboard.writeText(caption).catch(() => {});
    setInstagramCopied(true);
    setTimeout(() => setInstagramCopied(false), 3000);
  };

  const handleSmsOpen = () => {
    // sms: scheme — phone number is optional; works on iOS & Android
    const number   = smsPhone.replace(/[\s\-().+]/g, '');
    const body     = encodeURIComponent(`${shareText}\n\n${shareUrl}`);
    const href     = number ? `sms:+${number}?body=${body}` : `sms:?body=${body}`;
    window.location.href = href;
    setTimeout(() => { setShowSmsInput(false); setSmsPhone(''); }, 500);
  };

  const handleNative = () => {
    if (navigator.share) {
      navigator.share({ title, text: shareText, url: shareUrl }).catch(() => {});
    }
  };

  const renderPlatformBtn = (p) => {
    // Instagram — copy caption
    if (p.id === 'instagram') {
      return (
        <button
          key={p.id}
          className="share-platform-btn"
          style={{ '--pc': p.color }}
          onClick={handleInstagram}
        >
          <span className="share-platform-emoji">{p.emoji}</span>
          <span className="share-platform-name">
            {instagramCopied ? '✓ Copied!' : p.name}
          </span>
        </button>
      );
    }
    // SMS — toggle phone input
    if (p.id === 'sms') {
      return (
        <button
          key={p.id}
          className={`share-platform-btn ${showSmsInput ? 'share-platform-btn--active' : ''}`}
          style={{ '--pc': p.color }}
          onClick={() => setShowSmsInput(s => !s)}
        >
          <span className="share-platform-emoji">{p.emoji}</span>
          <span className="share-platform-name">{p.name}</span>
        </button>
      );
    }
    // Standard link platforms
    return (
      <a
        key={p.id}
        href={p.href}
        target="_blank"
        rel="noopener noreferrer"
        className="share-platform-btn"
        style={{ '--pc': p.color }}
        onClick={() => setTimeout(onClose, 300)}
      >
        <span className="share-platform-emoji">{p.emoji}</span>
        <span className="share-platform-name">{p.name}</span>
      </a>
    );
  };

  return (
    <>
      <div className="share-backdrop" onClick={onClose} />
      <div className="share-modal" role="dialog" aria-label="Share">

        {/* Header */}
        <div className="share-modal-header">
          <h3 className="share-modal-title-text">Share this lesson</h3>
          <button className="share-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Content preview */}
        <div className="share-preview">
          <span className="share-preview-label">🎓 BiteLearning</span>
          <p className="share-preview-title">{title}</p>
          {description && <p className="share-preview-desc">{description}</p>}
        </div>

        {view === 'main' ? (
          <>
            {/* ── Community option ── */}
            {content && (
              <button
                className="share-community-btn"
                onClick={() => setView('community')}
              >
                <span className="share-community-btn-icon">🌐</span>
                <div className="share-community-btn-text">
                  <span className="share-community-btn-label">Share inside BiteLearning</span>
                  <span className="share-community-btn-sub">Post to one of your communities</span>
                </div>
                <span className="share-community-btn-arrow">›</span>
              </button>
            )}

            {content && (
              <div className="share-divider"><span>or share externally</span></div>
            )}

            {/* ── All 8 platforms (4 × 2 grid) ── */}
            <div className="share-platforms">
              {platforms.map(renderPlatformBtn)}
            </div>

            {/* ── SMS phone-number input (expands when SMS button clicked) ── */}
            {showSmsInput && (
              <div className="share-sms-panel">
                <p className="share-sms-label">
                  📱 Enter a mobile number (or leave blank to open your SMS app):
                </p>
                <div className="share-sms-row">
                  <input
                    type="tel"
                    className="share-sms-input"
                    placeholder="+1 555 123 4567"
                    value={smsPhone}
                    onChange={e => setSmsPhone(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSmsOpen()}
                    autoFocus
                  />
                  <button className="share-copy-btn" onClick={handleSmsOpen}>
                    Send
                  </button>
                </div>
                <p className="share-sms-hint">
                  Opens your device's SMS app with the message pre-filled.
                </p>
              </div>
            )}

            {/* ── Instagram hint ── */}
            {instagramCopied && (
              <p className="share-instagram-hint">
                Caption copied — open Instagram and paste it in your post or story!
              </p>
            )}

            {/* ── Native share (mobile) ── */}
            {typeof navigator.share === 'function' && (
              <button className="share-native-btn" onClick={handleNative}>
                📤 Open system share sheet…
              </button>
            )}

            {/* ── Copy link ── */}
            <div className="share-copy-row">
              <input
                readOnly
                value={shareUrl}
                className="share-copy-input"
                onFocus={e => e.target.select()}
              />
              <button className="share-copy-btn" onClick={handleCopyLink}>
                {copied ? '✓ Copied!' : 'Copy link'}
              </button>
            </div>
          </>
        ) : (
          <CommunityPickerView
            content={content}
            onBack={() => setView('main')}
            onClose={onClose}
          />
        )}

      </div>
    </>
  );
}

export default ShareModal;
